import React, { useEffect, useState } from "react";
import { FileDown, Mail, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, formatApiError } from "../lib/api";
import { mk, money } from "../lib/market";

const day = (s) => new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const EMAIL = { sent: "Emailed", mocked: "Email in test mode", failed: "Email failed", skipped: "No email on file", pending: "Sending…" };

function InvoiceRow({ inv, sale }) {
  const [busy, setBusy] = useState(false);
  async function resend() {
    setBusy(true);
    try { const { data } = await mk.post(`/invoices/${inv.invoice_id}/resend`); toast.success(EMAIL[data.emailed.buyer] || "Sent"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`invoice-${inv.invoice_id}`}>
      <div className="w-11 h-11 rounded-full bg-[var(--bg-warm)] flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-[var(--gold)]" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">{inv.source_label} · {inv.number} · {day(inv.issued_at)}</p>
        <p className="font-serif text-xl truncate" data-testid={`invoice-title-${inv.invoice_id}`}>{inv.lines.map((l) => l.description).join(", ")}</p>
        <p className="text-xs text-[var(--muted)] truncate">{sale ? `From ${inv.buyer_name || inv.buyer_email || "a guest"}` : `Paid to ${inv.recipient_label || "Locked In Forever"}`}{!sale && inv.emailed?.buyer ? ` · ${EMAIL[inv.emailed.buyer] || ""}` : ""}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-serif text-2xl" data-testid={`invoice-total-${inv.invoice_id}`}>{money(sale ? inv.earnings : inv.total)}</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700">{sale ? "Your earnings" : "Paid"}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <a href={`${BACKEND_URL}${inv.pdf_url}`} target="_blank" rel="noreferrer" className="btn-outline-gold !py-2 !px-4 text-sm" data-testid={`invoice-pdf-${inv.invoice_id}`}><FileDown className="w-4 h-4" /> PDF</a>
        {!sale && <button onClick={resend} disabled={busy} className="btn-outline-gold !py-2 !px-3" aria-label="Email me this invoice" data-testid={`invoice-resend-${inv.invoice_id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}</button>}
      </div>
    </div>
  );
}

export function InvoicesView({ admin = false }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("purchases");
  useEffect(() => {
    const url = admin ? "/admin/invoices" : "/invoices/mine";
    mk.get(url).then((r) => {
      const data = admin ? { purchases: r.data.invoices, sales: [], spent: r.data.total } : r.data;
      setD(data);
      if (!data.purchases.length && data.sales.length) setTab("sales");
    }).catch(() => setD({ purchases: [], sales: [], spent: 0, earned: 0 }));
  }, [admin]);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" />;
  const rows = tab === "sales" ? d.sales : d.purchases;
  const tabs = admin ? [["purchases", `All invoices (${d.purchases.length})`]] : [["purchases", `My purchases (${d.purchases.length})`], ...(d.sales.length ? [["sales", `Sales & gifts received (${d.sales.length})`]] : [])];
  return (
    <div data-testid="invoices-view">
      <div className="grid grid-cols-2 gap-3 mb-6 max-w-lg">
        <div className="bg-white border border-[var(--line)] rounded-2xl p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">{admin ? "Total billed" : "Total spent"}</p><p className="font-serif text-3xl" data-testid="invoices-spent">{money(d.spent)}</p></div>
        {!admin && <div className="bg-white border border-[var(--line)] rounded-2xl p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">Earned</p><p className="font-serif text-3xl" data-testid="invoices-earned">{money(d.earned)}</p></div>}
      </div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${tab === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`} data-testid={`invoices-tab-${k}`}>{l}</button>)}
      </div>
      {!rows.length && <p className="font-serif text-2xl text-[var(--muted)]" data-testid="invoices-empty">No invoices yet. Every paid order, gift and plan shows up here with a PDF receipt.</p>}
      <div className="space-y-3">{rows.map((inv) => <InvoiceRow key={inv.invoice_id + tab} inv={inv} sale={tab === "sales"} />)}</div>
    </div>
  );
}
